// ============================================================================
// Bases y Propiedades — piezas de la consola operativa (estilo iVMS-4200)
// ============================================================================
// Árbol de sitios, KPIs, avatares de iniciales, galería, drawer de detalle y
// bloques de carga/vacío/error. Sólo tokens semánticos (bg-card, text-content,
// border-line…): funcionan igual en claro y en oscuro.
// ============================================================================

import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Spinner, EmptyState, Button, cn, type Tone } from "../../../components/ui";

// ---------------------------------------------------------------------------
// Utilidades
// ---------------------------------------------------------------------------

/** Miniatura de una foto del Sandbox (`/sandbox/x/foto.webp` → `foto-thumb.webp`). */
export function thumbOf(url: string): string {
  return /^\/sandbox\/.+\.webp$/.test(url) && !url.endsWith("-thumb.webp") ? url.replace(/\.webp$/, "-thumb.webp") : url;
}

/** «hace 3 h» / «in 2 days» con Intl, en el idioma activo. */
export function relativeTime(value: string | null | undefined, lng: string, nowMs = Date.now()): string {
  if (!value) return "—";
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return "—";
  const diff = (ms - nowMs) / 1000;
  const abs = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat(lng.startsWith("en") ? "en" : "es", { numeric: "auto" });
  if (abs < 60) return rtf.format(Math.round(diff), "second");
  if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), "hour");
  return rtf.format(Math.round(diff / 86400), "day");
}

/** Fecha y hora cortas en el idioma activo (hora de México). */
export function shortDateTime(value: string | null | undefined, lng: string): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(lng.startsWith("en") ? "en-US" : "es-MX", {
    timeZone: "America/Mexico_City",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function timeOnly(value: string | null | undefined, lng: string): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString(lng.startsWith("en") ? "en-US" : "es-MX", { timeZone: "America/Mexico_City", hour: "2-digit", minute: "2-digit" });
}

/** Tono por % de cumplimiento u ocupación. */
export function pctTone(pct: number | null | undefined, invert = false): Tone {
  if (pct === null || pct === undefined) return "neutral";
  const good = invert ? pct <= 60 : pct >= 90;
  const mid = invert ? pct <= 85 : pct >= 70;
  return good ? "success" : mid ? "warning" : "error";
}

const TONE_TEXT: Record<Tone, string> = {
  neutral: "text-content-secondary",
  primary: "text-primary-500",
  success: "text-success",
  warning: "text-warning",
  error: "text-error",
  info: "text-info",
};
const TONE_BG: Record<Tone, string> = {
  neutral: "bg-content-muted",
  primary: "bg-primary-500",
  success: "bg-success",
  warning: "bg-warning",
  error: "bg-error",
  info: "bg-info",
};

// ---------------------------------------------------------------------------
// KPI
// ---------------------------------------------------------------------------

export function Kpi({ icon, label, value, hint, tone = "primary", progress }: { icon: string; label: string; value: ReactNode; hint?: ReactNode; tone?: Tone; progress?: number | null }) {
  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-xl border border-line bg-card p-3.5">
      <div className="flex items-center gap-2">
        <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-subtle", TONE_TEXT[tone])}>
          <i className={cn(icon, "text-base")} />
        </span>
        <span className="truncate text-xs font-medium text-content-secondary">{label}</span>
      </div>
      <div className="text-2xl font-bold leading-none text-content tabular-nums">{value}</div>
      {progress !== undefined && progress !== null ? <ProgressBar pct={progress} tone={tone} /> : null}
      {hint ? <div className="truncate text-[11px] text-content-muted">{hint}</div> : null}
    </div>
  );
}

export function ProgressBar({ pct, tone = "primary", className }: { pct: number; tone?: Tone; className?: string }) {
  const v = Math.max(0, Math.min(100, Math.round(pct)));
  return (
    <div className={cn("h-1.5 w-full overflow-hidden rounded-full bg-subtle", className)} role="progressbar" aria-valuenow={v} aria-valuemin={0} aria-valuemax={100}>
      <div className={cn("h-full rounded-full transition-all", TONE_BG[tone])} style={{ width: `${v}%` }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Avatar de iniciales (nada de fotos de personas)
// ---------------------------------------------------------------------------

const AVATAR_TINTS = ["bg-primary-500/15 text-primary-500", "bg-success/15 text-success", "bg-warning/15 text-warning", "bg-info/15 text-info", "bg-error/15 text-error"];

export function initialsOf(name: string): string {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return ((parts[0][0] ?? "") + (parts.length > 1 ? parts[1][0] ?? "" : "")).toUpperCase();
}

export function PersonAvatar({ name, size = "md", className }: { name: string; size?: "sm" | "md" | "lg"; className?: string }) {
  let h = 0;
  for (const ch of name || "") h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  const sz = size === "sm" ? "h-6 w-6 text-[10px]" : size === "lg" ? "h-10 w-10 text-sm" : "h-8 w-8 text-xs";
  return (
    <span title={name} className={cn("inline-flex shrink-0 items-center justify-center rounded-full font-semibold ring-2 ring-card", sz, AVATAR_TINTS[h % AVATAR_TINTS.length], className)}>
      {initialsOf(name)}
    </span>
  );
}

/**
 * Grupo de avatares de iniciales: tamaño fijo, cada uno sobre una base OPACA
 * (los tonos son translúcidos y, encimados, las iniciales se mezclaban hasta ser
 * ilegibles), anillo del color de la tarjeta que los separa, máximo 3 visibles y
 * luego «+N». El título del grupo y del «+N» lleva los nombres completos.
 */
export function AvatarStack({ names, max = 3 }: { names: string[]; max?: number }) {
  const limit = Math.max(1, max);
  const shown = names.slice(0, limit);
  const rest = names.slice(limit);
  return (
    <span className="inline-flex shrink-0 items-center -space-x-0.5" title={names.join(", ")} aria-label={names.join(", ")} data-avatar-stack="">
      {shown.map((n, i) => (
        <span key={`${n}-${i}`} className="relative inline-flex h-6 w-6 shrink-0 rounded-full bg-card" style={{ zIndex: shown.length - i }}>
          <PersonAvatar name={n} size="sm" />
        </span>
      ))}
      {rest.length ? (
        <span title={rest.join(", ")} className="relative inline-flex h-6 min-w-[1.5rem] shrink-0 items-center justify-center rounded-full bg-subtle px-1 text-[10px] font-semibold tabular-nums text-content-secondary ring-2 ring-card">
          +{rest.length}
        </span>
      ) : null}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Galería
// ---------------------------------------------------------------------------

export function Photo({ src, alt, className, thumb = false, icon = "ri-image-line" }: { src?: string | null; alt: string; className?: string; thumb?: boolean; icon?: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (!src || failed) {
    return (
      <div className={cn("flex items-center justify-center bg-subtle text-content-faint", className)} aria-label={alt}>
        <i className={cn(icon, "text-3xl")} />
      </div>
    );
  }
  return <img src={thumb ? thumbOf(src) : src} alt={alt} loading="lazy" onError={() => setFailed(true)} className={cn("object-cover", className)} />;
}

export function PhotoGallery({ photos, alt }: { photos: string[]; alt: string }) {
  const [active, setActive] = useState(0);
  useEffect(() => setActive(0), [photos]);
  if (!photos.length) return <Photo alt={alt} className="aspect-[16/10] w-full rounded-xl" />;
  return (
    <div className="space-y-2">
      <Photo src={photos[active]} alt={alt} className="aspect-[16/10] w-full rounded-xl" />
      {photos.length > 1 ? (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {photos.map((p, i) => (
            <button key={`${p}-${i}`} type="button" onClick={() => setActive(i)} aria-label={`${alt} ${i + 1}`} className={cn("shrink-0 overflow-hidden rounded-lg border-2", i === active ? "border-primary-500" : "border-transparent opacity-70 hover:opacity-100")}>
              <Photo src={p} alt={`${alt} ${i + 1}`} thumb className="h-12 w-20" />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drawer de detalle
// ---------------------------------------------------------------------------

export function DetailDrawer({ open, title, subtitle, onClose, actions, children }: { open: boolean; title: ReactNode; subtitle?: ReactNode; onClose: () => void; actions?: ReactNode; children: ReactNode }) {
  const { t } = useTranslation("bases");
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[65] flex justify-end" role="dialog" aria-modal="true">
      <button type="button" aria-label={t("common.close")} className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" onClick={onClose} />
      <aside className="relative flex h-full w-full max-w-xl flex-col border-l border-line bg-surface shadow-2xl">
        <header className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-content">{title}</h2>
            {subtitle ? <div className="mt-0.5 text-xs text-content-secondary">{subtitle}</div> : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {actions}
            <button type="button" onClick={onClose} aria-label={t("common.close")} className="flex h-8 w-8 items-center justify-center rounded-lg text-content-secondary hover:bg-subtle hover:text-content">
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
      </aside>
    </div>
  );
}

export function DetailRow({ icon, label, children }: { icon: string; label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <i className={cn(icon, "mt-0.5 text-base text-content-muted")} />
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wide text-content-muted">{label}</div>
        <div className="text-sm text-content">{children}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Árbol de sitios (izquierda, colapsable)
// ---------------------------------------------------------------------------

export interface TreeNode {
  id: string;
  label: string;
  icon?: string;
  count?: number;
  dotTone?: Tone;
  children?: TreeNode[];
}

export function SiteTree({ title, nodes, selected, onSelect, allLabel }: { title: string; nodes: TreeNode[]; selected: string | null; onSelect: (id: string | null) => void; allLabel: string }) {
  const { t } = useTranslation("bases");
  const [collapsed, setCollapsed] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [q, setQ] = useState("");
  const match = (n: TreeNode): boolean => !q || n.label.toLowerCase().includes(q.toLowerCase()) || Boolean(n.children?.some(match));

  if (collapsed) {
    return (
      <div className="hidden w-10 shrink-0 flex-col items-center border-r border-line bg-surface py-3 lg:flex">
        <button type="button" onClick={() => setCollapsed(false)} aria-label={t("common.expandTree")} title={t("common.expandTree")} className="flex h-8 w-8 items-center justify-center rounded-lg text-content-secondary hover:bg-subtle">
          <i className="ri-side-bar-line" />
        </button>
      </div>
    );
  }

  const row = (n: TreeNode, depth: number) => {
    const hasKids = Boolean(n.children?.length);
    const isOpen = open[n.id] ?? depth === 0;
    return (
      <li key={n.id}>
        <div className={cn("group flex items-center gap-1 rounded-lg pr-2 text-sm", selected === n.id ? "bg-primary-500/10 text-primary-500" : "text-content hover:bg-subtle")} style={{ paddingLeft: 4 + depth * 14 }}>
          {hasKids ? (
            <button type="button" onClick={() => setOpen((o) => ({ ...o, [n.id]: !isOpen }))} aria-label={isOpen ? t("common.collapse") : t("common.expand")} className="flex h-6 w-5 items-center justify-center text-content-muted">
              <i className={isOpen ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"} />
            </button>
          ) : (
            <span className="w-5" />
          )}
          <button type="button" onClick={() => onSelect(n.id)} className="flex min-w-0 flex-1 items-center gap-2 py-1.5 text-left">
            {n.dotTone ? <span className={cn("h-2 w-2 shrink-0 rounded-full", TONE_BG[n.dotTone])} /> : null}
            {n.icon ? <i className={cn(n.icon, "shrink-0 text-content-muted")} /> : null}
            <span className="truncate">{n.label}</span>
            {n.count !== undefined ? <span className="ml-auto shrink-0 text-[11px] text-content-muted tabular-nums">{n.count}</span> : null}
          </button>
        </div>
        {hasKids && isOpen ? <ul>{n.children!.filter(match).map((c) => row(c, depth + 1))}</ul> : null}
      </li>
    );
  };

  return (
    <aside className="hidden w-64 shrink-0 flex-col border-r border-line bg-surface lg:flex">
      <div className="flex items-center justify-between gap-2 border-b border-line px-3 py-2.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-content-secondary">{title}</span>
        <button type="button" onClick={() => setCollapsed(true)} aria-label={t("common.collapseTree")} title={t("common.collapseTree")} className="flex h-7 w-7 items-center justify-center rounded-lg text-content-secondary hover:bg-subtle">
          <i className="ri-side-bar-fill" />
        </button>
      </div>
      <div className="px-3 py-2">
        <div className="relative">
          <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-content-faint" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("common.filterTree")} className="w-full rounded-lg border border-line bg-app py-1.5 pl-8 pr-2 text-xs text-content outline-none placeholder:text-content-faint focus:border-primary-500" />
        </div>
      </div>
      <ul className="flex-1 overflow-y-auto px-2 pb-3">
        <li>
          <button type="button" onClick={() => onSelect(null)} className={cn("flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm", selected === null ? "bg-primary-500/10 text-primary-500" : "text-content hover:bg-subtle")}>
            <i className="ri-stack-line" />
            <span className="truncate">{allLabel}</span>
          </button>
        </li>
        {nodes.filter(match).map((n) => row(n, 0))}
      </ul>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Estados de carga, error y vacío
// ---------------------------------------------------------------------------

export function LoadBlock({ status, error, onRetry, empty, emptyIcon, emptyTitle, emptyText, emptyAction, children }: { status: string; error?: string | null; onRetry?: () => void; empty: boolean; emptyIcon?: string; emptyTitle: string; emptyText?: string; emptyAction?: ReactNode; children: ReactNode }) {
  const { t } = useTranslation("bases");
  if (status === "loading" || status === "idle") {
    return (
      <div className="flex min-h-64 flex-1 items-center justify-center gap-3 text-sm text-content-secondary">
        <Spinner /> {t("common.loading")}
      </div>
    );
  }
  if (status === "error") {
    return (
      <EmptyState
        icon="ri-error-warning-line"
        title={t("common.loadError")}
        description={error || undefined}
        action={onRetry ? <Button variant="secondary" onClick={onRetry} icon={<i className="ri-refresh-line" />}>{t("common.retry")}</Button> : undefined}
      />
    );
  }
  if (empty) return <EmptyState icon={emptyIcon} title={emptyTitle} description={emptyText} action={emptyAction} />;
  return <>{children}</>;
}

/** Barra de herramientas superior de cada vista. */
export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface px-4 py-2.5">{children}</div>;
}

export function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="relative min-w-48 flex-1 sm:max-w-xs">
      <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-content-faint" />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full rounded-lg border border-line bg-app py-1.5 pl-8 pr-2 text-sm text-content outline-none placeholder:text-content-faint focus:border-primary-500" />
    </div>
  );
}

export function FilterSelect<T extends string>({ value, onChange, options, label }: { value: T | ""; onChange: (v: T | "") => void; options: Array<{ value: T; label: string }>; label: string }) {
  return (
    <select aria-label={label} value={value} onChange={(e) => onChange(e.target.value as T | "")} className="rounded-lg border border-line bg-app px-2 py-1.5 text-sm text-content outline-none focus:border-primary-500">
      <option value="">{label}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export const VIEW_KINDS = ["grid", "list", "table"] as const;
export type ViewKind = (typeof VIEW_KINDS)[number];

export function ViewSwitch({ value, onChange }: { value: ViewKind; onChange: (v: ViewKind) => void }) {
  const { t } = useTranslation("bases");
  const icons: Record<ViewKind, string> = { grid: "ri-layout-grid-line", list: "ri-list-unordered", table: "ri-table-line" };
  return (
    <div className="flex rounded-lg border border-line bg-card p-0.5" role="group">
      {VIEW_KINDS.map((k) => (
        <button key={k} type="button" onClick={() => onChange(k)} aria-pressed={value === k} title={t(`common.view.${k}`)} aria-label={t(`common.view.${k}`)} className={cn("flex h-7 w-8 items-center justify-center rounded-md", value === k ? "bg-primary-500 text-on-primary" : "text-content-secondary hover:bg-subtle")}>
          <i className={icons[k]} />
        </button>
      ))}
    </div>
  );
}

/** Campo de formulario simple (label + control). */
export function FormField({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={cn("block", className)}>
      <span className="mb-1 block text-xs font-medium text-content-secondary">{label}</span>
      {children}
    </label>
  );
}

export const inputCls = "w-full rounded-lg border border-line bg-app px-3 py-2 text-sm text-content outline-none placeholder:text-content-faint focus:border-primary-500";
